<template>
  <button
    type="button"
    class="tw-block tw-w-full tw-cursor-pointer tw-rounded tw-border tw-border-solid tw-border-gray-200 tw-bg-white tw-p-0 tw-text-left tw-text-inherit hover:tw-border-gray-400"
    data-test="compliance-review-hire"
    @click="emit('select')"
  >
    <WdsStack direction-column gap-sm class="tw-p-3.5">
      <WdsStack direction-column gap-xs>
        <WdsStack align-center gap-sm>
          <HireActiveStatusLabel :status="hire.activeStatus" sm />
          <WdsText v-if="hire.number" sm secondary class="tw-tabular-nums">{{ `#${hire.number}` }}</WdsText>
        </WdsStack>
        <WdsText class="tw-font-medium">{{ hire.job.name }}</WdsText>
        <WdsText
          v-if="contract?.jobDescription"
          sm
          secondary
          class="tw-line-clamp-2 tw-whitespace-pre-wrap"
          data-test="compliance-review-hire-description"
        >
          {{ contract.jobDescription }}
        </WdsText>
      </WdsStack>

      <WdsStack align-center gap-xs class="tw-flex-wrap">
        <WdsIcon name="calendar" :size="16" color="#757585" />
        <WdsText sm data-test="compliance-review-hire-dates">
          <HireDateRange :start="timeLine.start" :end="timeLine.end" :pending-extension="hire.pendingExtension" />
        </WdsText>
        <WdsText v-if="timing" sm secondary data-test="compliance-review-hire-timing">
          {{ timing }}
        </WdsText>
      </WdsStack>
    </WdsStack>

    <WdsStack
      v-if="progress.total > 0"
      align-center
      gap-sm
      class="tw-border-0 tw-border-t tw-border-solid tw-border-gray-100 tw-px-3.5 tw-py-2.5"
    >
      <WdsIcon name="task" :size="16" color="#757585" />
      <ChecklistProgress :done="progress.done" :total="progress.total" class="tw-flex-1" />
      <WdsText xs secondary class="tw-flex-none tw-tabular-nums" data-test="compliance-review-hire-progress">
        {{ $t(':done of :total done', { done: progress.done, total: progress.total }) }}
      </WdsText>
    </WdsStack>
  </button>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import moment from 'moment'
import { WdsIcon, WdsStack, WdsText } from '@worksome/wds-components'
import ChecklistProgress from '@/components/Checklists/ChecklistProgress.vue'
import { sumCheckCounts } from '@/components/Checklists/checkCounts'
import HireActiveStatusLabel from '@/components/Common/HireActiveStatusLabel.vue'
import HireDateRange from '@/components/Hire/HireDateRange.vue'
import { ComplianceReviewHireFragment } from '@/components/compliances/ComplianceReviewModal/fragments'
import { useHireContract } from '@/components/Hire/composables/useHireContract'
import { $t, $tc } from '@/app-bootstrapping/SetUpInternationalization'
import { useContractTimeLine } from '@/composables/useContractTimeLine'
import { FragmentType, useFragment } from '@/gql'
import { HireActiveStatus } from '@/gql/graphql'

const props = defineProps<{ hire: FragmentType<typeof ComplianceReviewHireFragment> }>()

const emit = defineEmits<{ select: [] }>()

const hire = computed(() => useFragment(ComplianceReviewHireFragment, props.hire))

const contract = useHireContract(hire)

const timeLine = useContractTimeLine(contract)

const progress = computed(() => sumCheckCounts(hire.value.checklists))

const daysUntil = (date: string) => moment(date).startOf('day').diff(moment().startOf('day'), 'days')

// Only a hire that is running or about to start has a countdown worth showing, and a proposed extension replaces the end one.
const timing = computed(() => {
  const { activeStatus } = hire.value

  if (activeStatus === HireActiveStatus.Active && contract.value?.endDate) {
    if (hire.value.pendingExtension) return ''

    const days = daysUntil(contract.value.endDate)

    return days > 0 ? $tc('Ends in :count day | Ends in :count days', days) : ''
  }

  if (activeStatus === HireActiveStatus.Ready && contract.value?.startDate) {
    const days = daysUntil(contract.value.startDate)

    return days > 0 ? $tc('Starts in :count day | Starts in :count days', days) : ''
  }

  return ''
})
</script>
